import { useState, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionMenu, Icon, useCopyToClipboard } from '@hotjar/hotsauce';
import type { PageVisitEvent, Trace } from '@hotjar/webapp/features/Playbacks/types';
import { Time } from './Time';
import { extractErrorTypeByText } from '../utils/extractErrorTypeByText';
import { getErrorTextToCopy } from '../utils/getErrorTextToCopy';
import {
  ErrorLine,
  IconContainer,
  DropdownButton,
  TextContainer,
  ErrorText,
  ErrorStack,
  StackRow,
  ErrorActionMenu,
} from './styles';

const InternalErrorLog = ({
  event,
  onTrendNavigationClick,
  onCopyActionClick,
}: {
  event: PageVisitEvent;
  onTrendNavigationClick: (errorText: string) => void;
  onCopyActionClick: () => void;
}): JSX.Element => {
  const { t } = useTranslation();

  const errorText = event.text || '-';
  const [isOpen, setOpen] = useState(false);
  const onClick = useCallback(() => setOpen(!isOpen), [isOpen]);
  const getCopyText = useCallback(
    (errorText: string, trace: Trace | undefined, time: number): string =>
      getErrorTextToCopy(
        errorText,
        trace,
        time,
        t('common:player.error-log-actions.view-recording'),
      ),
    [t],
  );
  const { isCopied, copy } = useCopyToClipboard(
    getCopyText(errorText, event.trace, event.time_in_timeline),
  );

  return (
    <ErrorLine data-time-in-timeline={event.time_in_timeline}>
      <IconContainer>
        <Icon icon="warning_alt" color="text_danger" size="m" />
      </IconContainer>

      {event.trace ? (
        <DropdownButton isOpen={isOpen} onClick={onClick}>
          <Icon icon="caret_down_alt" color="text_light" size="m" />
        </DropdownButton>
      ) : null}

      <TextContainer>
        <ErrorText onClick={onClick} clickable={!!event.trace}>
          {errorText};
        </ErrorText>

        {event.trace && isOpen ? (
          <ErrorStack>
            {event.trace.map((line, i) => (
              <StackRow key={i}>
                <td>{line.functionName}</td>
                <td>
                  @{' '}
                  <a href={line.fileName} target="_blank" rel="noopener noreferrer">
                    {line.fileName}:{line.lineNumber}
                  </a>
                </td>
              </StackRow>
            ))}
          </ErrorStack>
        ) : null}
      </TextContainer>

      <ErrorActionMenu description="">
        <ActionMenu.Item
          icon="trends"
          onClick={() => {
            const errorType = extractErrorTypeByText(event.text) ?? '-';
            onTrendNavigationClick(errorType);
          }}
          description={t('common:player.error-log-actions.view-trend')}
        />
        <ActionMenu.Item
          icon={isCopied ? 'check_alt' : 'copy'}
          onClick={() => {
            copy();
            onCopyActionClick();
          }}
          description={t('common:player.error-log-actions.copy-error')}
        />
      </ErrorActionMenu>

      <Time event={event} />
    </ErrorLine>
  );
};

export const ErrorLog = memo(InternalErrorLog);
